import classNames from 'classnames';
import {isNil} from 'lodash';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import type {WithRouterProps} from 'react-router';

import Assignment from './Assignment';
import type {Value as DropdownValue} from './FilterDropdown';
import FilterDropdown from './FilterDropdown';
import {Button, Icon, LoadingSpinner, SearchInput} from '@albert-io/atomic';
import TabEmptyState from '../TabEmptyState';
import useAssignments from './hooks/useAssignments';
import {useStudentHomepageStore} from '../useStudentHomepageStore';

const SEARCH_DEBOUNCE_MS = 500;

interface ExpandableSearchProps {
  disabled: boolean;
  onChange: (value: string) => void;
  onReset: () => void;
}

const ExpandableSearch = ({disabled, onChange, onReset}: ExpandableSearchProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const valueRef = useRef('');
  const isResettingRef = useRef(false);
  const isBlurringRef = useRef(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleIconClick = () => {
    setIsExpanded(true);
  };

  const handleChange = (_e: React.ChangeEvent<HTMLInputElement>, newValue: string) => {
    valueRef.current = newValue;
    onChange(newValue);
  };

  const handleReset = () => {
    isResettingRef.current = true;
    valueRef.current = '';
    onReset();
    requestAnimationFrame(() => {
      wrapperRef.current?.querySelector('input')?.focus();
      isResettingRef.current = false;
    });
  };

  const handleBlur = () => {
    isBlurringRef.current = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        isBlurringRef.current = false;
        if (!isResettingRef.current && valueRef.current === '') {
          const stillFocused = wrapperRef.current?.contains(document.activeElement);
          if (!stillFocused) {
            setIsExpanded(false);
          }
        }
      });
    });
  };

  // Re-focus the input when the search is expanded or re-enabled
  useEffect(() => {
    if (isExpanded && !disabled && !isBlurringRef.current) {
      const input = wrapperRef.current?.querySelector('input');
      if (input && document.activeElement !== input) {
        input.focus();
      }
    }
  }, [isExpanded, disabled]);

  if (!isExpanded) {
    return (
      <Button
        aria-label='Search assignments'
        className='cursor-pointer items-center rounded-x1 border border-primary p-x1-half'
        disabled={disabled}
        onClick={handleIconClick}
        variant='outlined'
        color='secondary'
      >
        <Icon icon='search' />
      </Button>
    );
  }

  return (
    <div ref={wrapperRef}>
      <SearchInput
        autoFocus
        aria-label='Search assignments'
        disabled={disabled}
        onBlur={handleBlur}
        onChange={handleChange}
        onReset={handleReset}
        placeholder='Search assignments'
        rectangle
        variant
      />
    </div>
  );
};
function getEmptyStateCopy(dropdownValue: DropdownValue, hasOtherAssignments: boolean): string {
  const defaultCopy = 'Your upcoming assignments will appear here.';
  if (!hasOtherAssignments) {
    return defaultCopy;
  }

  switch (dropdownValue) {
    case 'toDo':
      return 'No upcoming assignments.';
    case 'open':
      return 'No open assignments.';
    case 'closed':
      return 'No closed assignments.';
    default:
      return defaultCopy;
  }
}

export default function Assignments({params}: WithRouterProps) {
  const areClassroomsLoading = useStudentHomepageStore((state) => state.areClassroomsLoading);

  const classroomId: string | undefined = params.classroomId;

  const paginationTrigger = useRef<HTMLDivElement | null>(null);

  const [dropdownValue, setDropdownValue] = useState<DropdownValue>('toDo');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // This query fetches assignments with all status filter to determine whether the student
  // has any assignments at all — used to show contextual empty state messaging.
  const {assignments: anyAssignments, loading: loadingAll} = useAssignments(classroomId, 'all');

  const {assignments, fetchMore, isLoadingMore, loading, resetMoreAssignments} = useAssignments(
    classroomId,
    dropdownValue,
    debouncedSearchQuery
  );

  function onDropdownValueChange(newDropdownValue: DropdownValue) {
    setDropdownValue(newDropdownValue);
    resetMoreAssignments();
  }

  const isLoading = areClassroomsLoading || loading || loadingAll;
  const isDebouncing = searchQuery !== debouncedSearchQuery;
  const showContent = !isLoading && !isDebouncing;

  const fetchMoreRef = useRef(fetchMore);
  fetchMoreRef.current = fetchMore;

  const stableHandleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const entry = entries[0];
      if (dropdownValue !== 'toDo' && entry?.isIntersecting) {
        fetchMoreRef.current();
      }
    },
    [dropdownValue]
  );

  useEffect(() => {
    const localPaginationTrigger = paginationTrigger?.current;
    const root = document.querySelector('#tabContentContainer');

    const observer = new IntersectionObserver(stableHandleIntersection, {
      root,
      rootMargin: '0px 0px 200px 0px',
      threshold: 0
    });

    if (!isNil(localPaginationTrigger)) {
      observer.observe(localPaginationTrigger);
    }

    return () => {
      if (!isNil(localPaginationTrigger)) {
        observer.unobserve(localPaginationTrigger);
        observer.disconnect();
      }
    };
  }, [stableHandleIntersection]);

  return (
    <>
      <div className='relative flex flex-col gap-x2 overflow-hidden px-x1 py-x3 m:px-x4'>
        <div className='mb-x1 flex flex-wrap items-center gap-x1'>
          <FilterDropdown
            disabled={isLoading}
            isScopedToClassroom={classroomId !== undefined}
            onValueChange={onDropdownValueChange}
            value={dropdownValue}
          />
          <ExpandableSearch
            disabled={isLoading}
            onChange={setSearchQuery}
            onReset={() => setSearchQuery('')}
          />
        </div>
        {(isLoading || isDebouncing) && <LoadingSpinner bottomMarginLevel={0} size={3} />}
        {showContent && assignments.length === 0 && !debouncedSearchQuery && (
          <TabEmptyState
            copy={getEmptyStateCopy(dropdownValue, anyAssignments.length > 0)}
            icon={['fal', 'file-alt']}
            subtitle={
              anyAssignments.length > 0
                ? 'Expecting to see more assignments? Try changing the dropdown above.'
                : undefined
            }
          />
        )}
        {showContent && assignments.length === 0 && debouncedSearchQuery && (
          <TabEmptyState
            copy={`No assignments found with "${debouncedSearchQuery}".`}
            subtitle='Not seeing an assignment you’re expecting? Try changing the dropdown above.'
          />
        )}
        {showContent &&
          assignments.length > 0 &&
          assignments.map((assignment) => (
            <Assignment assignment={assignment} key={assignment.assignmentId} />
          ))}
        {showContent && (
          <div
            className={classNames(
              'absolute flex w-full justify-center transition-[bottom] duration-200',
              {
                'bottom-x3': isLoadingMore,
                '-bottom-x7': !isLoadingMore
              }
            )}
          >
            <div className='flex h-x7 w-x7 items-center rounded-full background-subtle p-x1 elevation-shadow-l2'>
              <LoadingSpinner bottomMarginLevel={0} size={2} />
            </div>
          </div>
        )}
      </div>
      <div ref={paginationTrigger} className='h-px' />
    </>
  );
}
